// AR Report — Single-attempt detail page.
//
// Deepest drill: shows everything we know about one specific charge attempt:
//   - status, retry_path, failure_category, paid_amount with tooltips
//   - subscriber + subscription state
//   - all attempts for this subscription (timeline) with this one highlighted
//   - time gaps between attempts (so ops can verify SC's cadence)
//   - SC's raw report row (every field SC included for this transaction)

import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type ARAttemptDetail } from '../api/client'
import { Hint, StatusBadge, RetryPathBadge, FailureCategoryBadge } from '../components/ARGlossary'

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
  })
}

function fmtHours(h: number | null): string {
  if (h == null) return '—'
  if (h < 1) return `${Math.round(h * 60)}m`
  if (h < 48) return `${h.toFixed(1)}h`
  const days = h / 24
  return `${days.toFixed(1)}d`
}

export function ARReportAttemptDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useQuery({
    queryKey: ['ar-attempt-detail', id],
    queryFn: () => api.get<ARAttemptDetail>(`/api/ar-report/attempt/${id}`),
    enabled: !!id,
  })

  if (isLoading) return <div className="p-8 label text-paper-trace">Loading…</div>
  if (!data) return <div className="p-8 label text-radar-red">Attempt not found.</div>

  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/ar-report" className="label-amber hover:underline">← AR Report</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">Attempt #{data.attempt_id}</span>
      </div>

      <header className="border-b border-ink-500 pb-4">
        <div className="label mb-1">Single attempt detail</div>
        <h1 className="font-serif text-[28px] leading-tight text-paper">
          AR attempt #{data.attempt_id}
        </h1>
        <p className="text-paper-dim text-[13px] mt-2 max-w-[800px]">
          Deepest drill: shows everything SC reported about this single
          charge attempt — status, retry path, failure reason, surrounding
          attempts on the same subscription, and the raw report row.
        </p>
      </header>

      {/* Headline facts */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Fact
          label="When"
          hint={{ title: 'Attempt timestamp', short: 'When SC processed this charge attempt (UTC).' }}
        >
          <span className="font-mono text-[13px]">{fmtDateTime(data.attempt_at)}</span>
        </Fact>
        <Fact
          label="Status"
          hint={{ title: 'SC outcome', short: 'The status string SC reported. Hover the badge for the full meaning.' }}
        >
          <StatusBadge status={data.status} />
        </Fact>
        <Fact
          label="Retry path"
          hint={{ title: 'Which retry cadence', short: 'fast = +22h bonus retry path (invalid_card_closed). standard = the +5/+3/+8/+6 schedule.' }}
        >
          {data.retry_path ? <RetryPathBadge path={data.retry_path} /> : <span className="text-paper-trace text-[12px]">— not a failure</span>}
        </Fact>
        <Fact
          label="Reason"
          hint={{ title: 'Failure category', short: 'Our classification of the SC details string into a recovery-routing category.' }}
        >
          {data.failure_category ? <FailureCategoryBadge category={data.failure_category} /> : <span className="text-paper-trace text-[12px]">—</span>}
        </Fact>
      </section>

      {/* Details + paid amount */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-ink-500 bg-ink-800/40 p-4">
          <div className="label text-paper-faint mb-2">
            <Hint title="SC details text" short="Raw free-text SC included on this attempt — usually the processor's decline message.">
              SC details
            </Hint>
          </div>
          <div className="font-mono text-[13px] text-paper-dim leading-relaxed">
            {data.details || <span className="text-paper-trace italic">— no details —</span>}
          </div>
        </div>
        <div className="border border-ink-500 bg-ink-800/40 p-4">
          <div className="label text-paper-faint mb-2">
            <Hint title="Paid amount" short="$ captured on this attempt. Always blank/0 for failures; non-zero only on CHARGED rows.">
              Paid amount
            </Hint>
          </div>
          <div className="font-mono text-[28px] tabular-nums text-paper">
            {data.paid_amount != null ? `$${data.paid_amount.toFixed(2)}` : <span className="text-paper-trace text-[16px]">—</span>}
          </div>
        </div>
      </section>

      {/* Subscriber + Subscription */}
      {(data.subscriber || data.subscription) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-ink-500 bg-ink-800/40 p-4">
            <div className="label text-paper-faint mb-2">Subscriber</div>
            {data.subscriber ? (
              <div className="space-y-1 font-mono text-[12px]">
                <Link to={`/subscribers/${data.subscriber.id}`} className="text-paper hover:text-amber-glow text-[14px]">
                  {data.subscriber.first_name} {data.subscriber.last_name}
                </Link>
                <div className="text-paper-dim">{data.subscriber.email ?? '—'}</div>
                <div className="text-paper-trace text-[11px]">SC account #{data.subscriber.simplecirc_account_id}</div>
              </div>
            ) : (
              <div className="text-paper-trace label">— Subscriber not linked to attempt —</div>
            )}
          </div>
          <div className="border border-ink-500 bg-ink-800/40 p-4">
            <div className="label text-paper-faint mb-2">Subscription</div>
            {data.subscription ? (
              <div className="space-y-1 font-mono text-[12px]">
                <div>
                  <span className="text-paper-faint">Status: </span>
                  <span className={data.subscription.status === 'Active' ? 'text-radar-green' : 'text-amber-glow'}>
                    {data.subscription.status}
                  </span>
                </div>
                <div>
                  <span className="text-paper-faint">AR enabled: </span>
                  <span className={data.subscription.auto_renew ? 'text-radar-green' : 'text-paper-trace'}>
                    {data.subscription.auto_renew ? 'yes' : 'no'}
                  </span>
                </div>
                {data.subscription.auto_renew_status && (
                  <div>
                    <span className="text-paper-faint">AR status: </span>
                    <span className="text-paper-dim">{data.subscription.auto_renew_status}</span>
                  </div>
                )}
                {data.subscription.current_period_end && (
                  <div>
                    <span className="text-paper-faint">Period ends: </span>
                    <span className="text-paper-dim">{data.subscription.current_period_end}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-paper-trace label">—</div>
            )}
          </div>
        </section>
      )}

      {/* Surrounding attempts timeline */}
      <section>
        <div className="flex items-baseline gap-3 mb-2">
          <h2 className="font-serif text-[20px] text-paper">Subscription's full attempt timeline</h2>
          <Hint
            title="Attempt timeline"
            short="Every AR attempt SC has fired for this subscription, oldest first."
            detail="The current attempt is highlighted in amber. Time gaps between attempts let you verify SC's actual cadence matches the +2/+5/+3/+8/+6 schedule (anchored on SC's processing day)."
          >
            <span className="label text-paper-trace">what is this?</span>
          </Hint>
        </div>
        <div className="border border-ink-500 bg-ink-800/40 overflow-hidden">
          <table className="w-full text-[12px] font-mono">
            <thead>
              <tr className="border-b border-ink-500/60 bg-ink-800/60">
                <th className="text-center px-3 py-2 label font-normal">A#</th>
                <th className="text-left px-3 py-2 label font-normal">When</th>
                <th className="text-left px-3 py-2 label font-normal">Status</th>
                <th className="text-left px-3 py-2 label font-normal">Path</th>
                <th className="text-left px-3 py-2 label font-normal">Reason</th>
                <th className="text-right px-3 py-2 label font-normal">$</th>
                <th className="text-left px-3 py-2 label font-normal">Details</th>
              </tr>
            </thead>
            <tbody>
              {data.surrounding_attempts.map((a, i) => {
                const prevAt = i > 0 ? data.surrounding_attempts[i - 1].attempt_at : null
                const gapHours = prevAt && a.attempt_at
                  ? (new Date(a.attempt_at).getTime() - new Date(prevAt).getTime()) / 3_600_000
                  : null
                return (
                  <tr
                    key={a.attempt_id}
                    className={`border-b border-ink-500/20 ${a.is_focus ? 'bg-amber-glow/5 border-l-2 border-l-amber-glow' : ''}`}
                  >
                    <td className="px-3 py-2 align-top text-center text-paper">{a.attempt_seq}</td>
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      <Link to={`/ar-report/attempt/${a.attempt_id}`} className="text-paper-dim hover:text-amber-glow">
                        {fmtDateTime(a.attempt_at)}
                      </Link>
                      {gapHours != null && (
                        <div className="text-paper-trace text-[10px]">+{fmtHours(gapHours)} from prior</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top"><StatusBadge status={a.status} /></td>
                    <td className="px-3 py-2 align-top"><RetryPathBadge path={a.retry_path} /></td>
                    <td className="px-3 py-2 align-top"><FailureCategoryBadge category={a.failure_category} /></td>
                    <td className="px-3 py-2 align-top text-right tabular-nums text-paper">
                      {a.paid_amount != null ? `$${a.paid_amount.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-paper-dim text-[11px] max-w-[300px] truncate" title={a.details ?? ''}>
                      {a.details ?? '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2 text-[11px] font-mono text-paper-trace">
          <div>
            <span className="text-paper-faint">Time since prior attempt: </span>
            <span className="text-paper-dim">{fmtHours(data.time_since_prior_hours)}</span>
          </div>
          <div>
            <span className="text-paper-faint">Time to next attempt: </span>
            <span className="text-paper-dim">{fmtHours(data.time_to_next_hours)}</span>
          </div>
        </div>
      </section>

      {/* Raw row */}
      <section>
        <div className="flex items-baseline gap-3 mb-2">
          <h2 className="font-serif text-[20px] text-paper">SC raw report row</h2>
          <Hint
            title="Raw report row"
            short="The exact row SC's AR Renewals Report exported for this attempt."
            detail="Every column SC included is here. Useful when SC's documented status codes don't match what we're seeing — you can see exactly what SC sent us."
          >
            <span className="label text-paper-trace">what is this?</span>
          </Hint>
        </div>
        <div className="border border-ink-500 bg-ink-800/40 p-4 overflow-x-auto">
          <table className="w-full text-[12px] font-mono">
            <tbody>
              {Object.entries(data.raw_row).map(([k, v]) => (
                <tr key={k} className="border-b border-ink-500/20 last:border-0">
                  <td className="px-2 py-1 align-top text-paper-faint w-[200px]">{k}</td>
                  <td className="px-2 py-1 align-top text-paper-dim break-all">
                    {typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
                      ? String(v)
                      : JSON.stringify(v)}
                  </td>
                </tr>
              ))}
              {Object.keys(data.raw_row).length === 0 && (
                <tr><td className="px-2 py-3 text-paper-trace label">no raw row stored</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function Fact({
  label,
  hint,
  children,
}: {
  label: string
  hint?: { title: string; short: string; detail?: string }
  children: React.ReactNode
}) {
  return (
    <div className="border border-ink-500 bg-ink-800/40 p-3">
      <div className="label text-paper-faint mb-1.5">
        {hint ? <Hint {...hint}>{label}</Hint> : label}
      </div>
      <div>{children}</div>
    </div>
  )
}
